import _, {flatMap} from 'lodash';
import {createSelector} from 'reselect';

import {buildTeammateDisplayName} from '../../../helpers/modelHelpers';
import {wrapInShallowSelector} from '../../../helpers/reselectHelpers';
import {AppState} from '../../appState';
import {getHiddenDelegationOwnerIds} from '../preferences/preferencesSelectors';
import {getFindTeammate} from '../teammates/lazyTeammatesSelectors';
import {getUserId} from '../teammates/teammatesSelectors';

/*
 * Delegations.
 */

/** Get a map of all delegations granted to our user by id. */
export function getDelegations(state: AppState) {
  return state.data.delegations;
}

/** Get a flat list of all delegations, unsorted and unfiltered. */
export const getFlatDelegations = createSelector(getDelegations, (delegations) =>
  _(delegations).values().compact().value(),
);

/** Get a flat list of delegations available to our user, sorted by owner name. */
export const getFlatUserDelegations = wrapInShallowSelector(
  createSelector([getFlatDelegations, getFindTeammate, getUserId], (delegations, findTeammate, userId) =>
    _(delegations)
      .filter((d) => d.teammateIds.includes(userId))
      .sortBy((d) => {
        const owner = findTeammate(d.ownerId, 'getFlatUserDelegations');
        return owner ? buildTeammateDisplayName(owner).toLowerCase() : '';
      })
      .value(),
  ),
);

export const getFlatDelegationOwners = wrapInShallowSelector(
  createSelector([getFlatDelegations, getFindTeammate, getUserId], (delegations, findTeammate, userId) =>
    _(delegations)
      .filter((d) => d.teammateIds.includes(userId))
      .map((d) => d.ownerId)
      .uniq()
      .map((id) => findTeammate(id, 'getFlatDelegationOwners'))
      .compact()
      .value(),
  ),
);

/** Get a set of inbox ids returned across all delegations. */
export const getDelegatedInboxIdsSet = createSelector(
  getFlatDelegations,
  (delegations) => new Set(flatMap(delegations, (d) => d.inboxIds)),
);

/** Get alphabetized list of visible delegations, based upon user preferences. */
export const getVisibleDelegations = createSelector(
  [getFlatUserDelegations, getHiddenDelegationOwnerIds],
  (delegations, hiddenDelegationOwnerIds) =>
    delegations.filter((d) => !hiddenDelegationOwnerIds.has(d.ownerId)),
);
